import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import config from "../config";
import apiClient from "../api/ResturantClient";
import NotificationCart from "./NotificationCart";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import SetupProgressBar from "./SetupProgressBar";
import LoadingSpinner from "./LoadingSpinner";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const MAP_LIBRARIES = ["places"];

const CreateRestaurant = () => {
  const [formData, setFormData] = useState({
    restaurant_name: "",
    address: "",
    email: "",
    phone: "",
    restaurant_type: "",
    number_of_tables: 0,
    opening_hours: {
      monday: { open: "", close: "", closing_day: "", pickup: false },
      tuesday: { open: "", close: "", closing_day: "", pickup: false },
      wednesday: { open: "", close: "", closing_day: "", pickup: false },
      thursday: { open: "", close: "", closing_day: "", pickup: false },
      friday: { open: "", close: "", closing_day: "", pickup: false },
      saturday: { open: "", close: "", closing_day: "", pickup: false },
      sunday: { open: "", close: "", closing_day: "", pickup: false },
    },
    city: "",
    country: "",
    state: "",
    zip_code: "",
    user_id: sessionStorage.getItem("user_id"),
    latitude: null,
    longitude: null,
  });

  const [stateOptions, setStateOptions] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [isNotification, setIsNotification] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place) {
        const { formatted_address, geometry, address_components } = place;
        const addressDetails = {
          address: formatted_address || "",
          latitude: geometry?.location?.lat(),
          longitude: geometry?.location?.lng(),
          city: "",
          state: "",
          country: "",
          zip_code: "",
        };

        address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality"))
            addressDetails.city = component.long_name;
          if (types.includes("administrative_area_level_1"))
            addressDetails.state = component.long_name;
          if (types.includes("country"))
            addressDetails.country = component.long_name;
          if (types.includes("postal_code"))
            addressDetails.zip_code = component.long_name;
        });

        setFormData((prevData) => ({
          ...prevData,
          ...addressDetails,
        }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("opening_hours.")) {
      const [_, day, field] = name.split(".");
      setFormData((prev) => {
        const updatedHours = {
          ...prev.opening_hours[day],
          [field]: type === "checkbox" ? checked : value,
        };
        if (field === "close") {
          const closingTime = value;
          const [hours, minutes] = closingTime.split(":");
          const isAM = parseInt(hours, 10) < 6;
          if (isAM) {
            const days = Object.keys(prev.opening_hours);
            const currentDayIndex = days.indexOf(day);
            const nextDay = days[(currentDayIndex + 1) % days.length];
            updatedHours.closing_day = nextDay;
          } else {
            updatedHours.closing_day = day;
          }
        }

        return {
          ...prev,
          opening_hours: {
            ...prev.opening_hours,
            [day]: updatedHours,
          },
        };
      });
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const requiredFields = [
      "restaurant_name",
      "email",
      "phone",
      "city",
      "country",
      "state",
      "zip_code",
      "restaurant_type",
      "number_of_tables",
    ];

    const allFieldsFilled = requiredFields.every((field) => {
      if (typeof formData[field] === "number") {
        return formData[field] >= 0;
      }
      return formData[field] && formData[field].toString().trim() !== "";
    });

    setIsFormComplete(allFieldsFilled);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/restaurant/create-restaurant`,
        formData
      );
      if (response.status === 200 || response.status === 201) {
        sessionStorage.setItem("restaurant_id", response.data.restaurant_id);
        localStorage.setItem(
          `restaurant_${response.data.restaurant_id}_menuSetup`,
          "pending"
        );
        localStorage.setItem("RestaurantToken", response.data.token);

        // Now link the restaurant with the user's subscription
        const userId = sessionStorage.getItem("user_id");
        try {
          // First try the link-restaurant-subscription endpoint which handles both user and subscription updates
          const linkResponse = await apiClient.post(
            `/subscription/link-restaurant-subscription`,
            { user_id: userId }
          );

          console.log(
            "Restaurant and subscription linked successfully:",
            linkResponse.data
          );
        } catch (linkError) {
          // If that fails, try the update-subscription-restaurant endpoint as fallback
          console.error(
            "Failed to link restaurant and subscription:",
            linkError
          );
          try {
            const updateResponse = await apiClient.post(
              `/subscription/update-subscription-restaurant`,
              { user_id: userId }
            );
            console.log(
              "Subscription updated with restaurant ID:",
              updateResponse.data
            );
          } catch (updateError) {
            console.error(
              "Failed to update subscription with restaurant ID:",
              updateError
            );
            // Continue with the flow even if linking fails
          }
        }
        toast.info(
          "Great! Your restaurant is created. Next steps:\n" +
            "1. Add menu items in Menu Management\n" +
            "2. Generate QR codes to let customers place orders easily",
          {
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            onClose: () => {
              navigate("/dashboard/menu-management", {
                state: {
                  restaurant_id: response.data.restaurant_id,
                },
              });
            },
          }
        );

        const navigationTimer = setTimeout(() => {
          navigate("/dashboard/menu-management", {
            state: {
              restaurant_id: response.data.restaurant_id,
            },
          });
        }, 10000);

        return () => clearTimeout(navigationTimer);
      }
    } catch (error) {
      setNotificationMessage("Error creating restaurant");
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isInitialLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <ToastContainer />
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="bg-white rounded-full p-3 shadow-lg inline-flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Create Your Restaurant
          </h1>
          <p className="text-xl text-gray-600">
            Set up your restaurant profile to get started
          </p>
        </div>

        <SetupProgressBar currentStep={1} />

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            {isNotification && (
              <NotificationCart
                message={notificationMessage}
                status={notificationStatus}
              />
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information Section */}
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
                  <h3 className="text-xl font-semibold text-gray-900">Basic Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Restaurant Name
                    </label>
                    <input
                      type="text"
                      name="restaurant_name"
                      value={formData.restaurant_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter restaurant name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="restaurant@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Restaurant Type
                    </label>
                    <input
                      type="text"
                      name="restaurant_type"
                      value={formData.restaurant_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="e.g., Italian, Mexican, Cafe"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information Section */}
              <div className="space-y-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-6 bg-green-500 rounded-full"></div>
                  <h3 className="text-xl font-semibold text-gray-900">Address Information</h3>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    {isLoaded && (
                      <Autocomplete
                        onLoad={setAutocomplete}
                        onPlaceChanged={handlePlaceSelect}
                      >
                        <input
                          type="text"
                          placeholder="Search your restaurant address"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </Autocomplete>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="State"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Zip code"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Restaurant Details Section */}
              <div className="space-y-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
                  <h3 className="text-xl font-semibold text-gray-900">Restaurant Details</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Tables
                    </label>
                    <input
                      type="number"
                      name="number_of_tables"
                      value={formData.number_of_tables}
                      onChange={handleChange}
                      required
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Opening Hours Section */}
              <div className="space-y-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-6 bg-orange-500 rounded-full"></div>
                  <h3 className="text-xl font-semibold text-gray-900">Opening Hours</h3>
                </div>

                <div className="space-y-4">
                  {Object.entries(formData.opening_hours).map(([day, hours]) => (
                    <div
                      key={day}
                      className="flex flex-col md:flex-row md:items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <span className="font-medium text-gray-700 w-32">
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </span>
                      
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600 whitespace-nowrap">Open:</label>
                          <input
                            type="time"
                            name={`opening_hours.${day}.open`}
                            value={hours.open}
                            onChange={handleChange}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          />
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600 whitespace-nowrap">Close:</label>
                          <input
                            type="time"
                            name={`opening_hours.${day}.close`}
                            value={hours.close}
                            onChange={handleChange}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          />
                        </div>
                        
                        <label className="flex items-center gap-2 ml-auto">
                          <input
                            type="checkbox"
                            name={`opening_hours.${day}.pickup`}
                            checked={hours.pickup}
                            onChange={handleChange}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all duration-200"
                          />
                          <span className="text-sm text-gray-600 whitespace-nowrap">
                            Pickup Available
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormComplete}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : isFormComplete
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:scale-105"
                      : "bg-gray-400 cursor-not-allowed"
                  } focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Restaurant...
                    </div>
                  ) : (
                    "Create Restaurant & Continue"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm">
            Need help setting up your restaurant?{" "}
            <button className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200">
              Contact Support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateRestaurant;